import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';

interface Transaction {
  id: string;
  type: 'earned' | 'spent';
  amount: number;
  currency: 'gold' | 'silver';
  source: string;
  timestamp: number;
}

interface Achievement {
  id: string;
  icon: string;
  title: string;
  description: string;
  reward: number;
  unlocked: boolean;
}

interface Reward {
  id: string;
  icon: string;
  title: string;
  cost: number;
  currency: 'gold' | 'silver';
}

const SCHOOL_REWARDS: Reward[] = [
  { id: 'r1', icon: '⏰', title: 'Extra Recess', cost: 50, currency: 'gold' },
  { id: 'r2', icon: '📝', title: 'Homework Pass', cost: 75, currency: 'gold' },
  { id: 'r3', icon: '🪑', title: 'Choose Your Seat', cost: 30, currency: 'gold' },
  { id: 'r4', icon: '🎁', title: 'Prize Box Pick', cost: 100, currency: 'gold' },
];

const HOME_REWARDS: Reward[] = [
  { id: 'h1', icon: '📱', title: '30min Screen Time', cost: 40, currency: 'silver' },
  { id: 'h2', icon: '🍕', title: 'Choose Dinner', cost: 60, currency: 'silver' },
  { id: 'h3', icon: '🌙', title: 'Stay Up Late', cost: 50, currency: 'silver' },
  { id: 'h4', icon: '🎮', title: 'Game Night Pick', cost: 80, currency: 'silver' },
];

const INITIAL_ACHIEVEMENTS: Achievement[] = [
  { id: 'a1', icon: '🔥', title: '7-Day Streak', description: 'Learn 7 days in a row', reward: 50, unlocked: false },
  { id: 'a2', icon: '⭐', title: 'First Gold Mastery', description: 'Master your first topic', reward: 30, unlocked: false },
  { id: 'a3', icon: '💯', title: 'Perfect Quiz', description: 'Score 100% on a quiz', reward: 25, unlocked: false },
  { id: 'a4', icon: '🚀', title: '1,000 XP', description: 'Earn 1,000 total XP', reward: 40, unlocked: false },
];

const TeacherSlide10RewardsDemo: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [goldBalance, setGoldBalance] = useState(0);
  const [silverBalance, setSilverBalance] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>(INITIAL_ACHIEVEMENTS);
  const [newAchievement, setNewAchievement] = useState<Achievement | null>(null);
  const [floatingCoin, setFloatingCoin] = useState<{ amount: number; currency: 'gold' | 'silver'; x: number; y: number } | null>(null);
  const [purchasedReward, setPurchasedReward] = useState<Reward | null>(null);

  const simIndex = useRef(0);

  // Simulation steps with proper typing
  type SimStep =
    | { action: 'earn_gold'; amount: number; source: string }
    | { action: 'earn_silver'; amount: number; source: string }
    | { action: 'achievement'; id: string }
    | { action: 'spend_gold'; rewardId: string }
    | { action: 'spend_silver'; rewardId: string };

  const simulationSteps: SimStep[] = [
    { action: 'earn_gold', amount: 15, source: 'Completed Lesson' },
    { action: 'earn_gold', amount: 20, source: 'Quiz Score 85%' },
    { action: 'earn_silver', amount: 10, source: 'Made Bed' },
    { action: 'achievement', id: 'a3' },
    { action: 'earn_gold', amount: 25, source: 'Perfect Quiz Bonus' },
    { action: 'earn_silver', amount: 15, source: 'Helped with Dishes' },
    { action: 'earn_gold', amount: 10, source: 'Streak Bonus' },
    { action: 'achievement', id: 'a1' },
    { action: 'earn_gold', amount: 50, source: '7-Day Streak Reward' },
    { action: 'earn_silver', amount: 20, source: 'Tidied Room' },
    { action: 'spend_gold', rewardId: 'r3' },
    { action: 'earn_gold', amount: 30, source: 'Mastery Achieved' },
    { action: 'achievement', id: 'a2' },
    { action: 'earn_gold', amount: 30, source: 'Mastery Reward' },
    { action: 'spend_silver', rewardId: 'h1' },
    { action: 'earn_gold', amount: 15, source: 'Daily Lesson' },
    { action: 'earn_silver', amount: 25, source: 'Did Homework Early' },
    { action: 'spend_gold', rewardId: 'r2' },
  ];

  useEffect(() => {
    if (!isPlaying) return;

    const timer = setTimeout(() => {
      if (simIndex.current >= simulationSteps.length) {
        simIndex.current = 0;
        // Reset for loop
        setGoldBalance(0);
        setSilverBalance(0);
        setTransactions([]);
        setAchievements(INITIAL_ACHIEVEMENTS);
        return;
      }

      const step = simulationSteps[simIndex.current];

      if (step.action === 'earn_gold') {
        setGoldBalance(prev => prev + step.amount);
        setFloatingCoin({ amount: step.amount, currency: 'gold', x: 50, y: 50 });
        const newTx: Transaction = {
          id: `t${Date.now()}`,
          type: 'earned',
          amount: step.amount,
          currency: 'gold',
          source: step.source,
          timestamp: Date.now(),
        };
        setTransactions(prev => [newTx, ...prev].slice(0, 8));
        setTimeout(() => setFloatingCoin(null), 1000);
      } else if (step.action === 'earn_silver') {
        setSilverBalance(prev => prev + step.amount);
        setFloatingCoin({ amount: step.amount, currency: 'silver', x: 50, y: 50 });
        const newTx: Transaction = {
          id: `t${Date.now()}`,
          type: 'earned',
          amount: step.amount,
          currency: 'silver',
          source: step.source,
          timestamp: Date.now(),
        };
        setTransactions(prev => [newTx, ...prev].slice(0, 8));
        setTimeout(() => setFloatingCoin(null), 1000);
      } else if (step.action === 'achievement') {
        const achievement = achievements.find(a => a.id === step.id);
        if (achievement && !achievement.unlocked) {
          setAchievements(prev => prev.map(a =>
            a.id === step.id ? { ...a, unlocked: true } : a
          ));
          setNewAchievement(achievement);
          setTimeout(() => setNewAchievement(null), 2000);
        }
      } else if (step.action === 'spend_gold') {
        const reward = SCHOOL_REWARDS.find(r => r.id === step.rewardId);
        if (reward && goldBalance >= reward.cost) {
          setGoldBalance(prev => prev - reward.cost);
          setPurchasedReward(reward);
          const newTx: Transaction = {
            id: `t${Date.now()}`,
            type: 'spent',
            amount: reward.cost,
            currency: 'gold',
            source: reward.title,
            timestamp: Date.now(),
          };
          setTransactions(prev => [newTx, ...prev].slice(0, 8));
          setTimeout(() => setPurchasedReward(null), 1500);
        }
      } else if (step.action === 'spend_silver') {
        const reward = HOME_REWARDS.find(r => r.id === step.rewardId);
        if (reward && silverBalance >= reward.cost) {
          setSilverBalance(prev => prev - reward.cost);
          setPurchasedReward(reward);
          const newTx: Transaction = {
            id: `t${Date.now()}`,
            type: 'spent',
            amount: reward.cost,
            currency: 'silver',
            source: reward.title,
            timestamp: Date.now(),
          };
          setTransactions(prev => [newTx, ...prev].slice(0, 8));
          setTimeout(() => setPurchasedReward(null), 1500);
        }
      }

      simIndex.current++;
    }, 1800);

    return () => clearTimeout(timer);
  }, [isPlaying, goldBalance, silverBalance, achievements]);

  const reset = () => {
    setIsPlaying(false);
    simIndex.current = 0;
    setGoldBalance(0);
    setSilverBalance(0);
    setTransactions([]);
    setAchievements(INITIAL_ACHIEVEMENTS);
    setNewAchievement(null);
    setFloatingCoin(null);
    setPurchasedReward(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-3 pb-2">
        <h2 className="text-2xl font-bold text-white">Rewards & Achievements System</h2>
        <p className="text-xs text-[#94a3b8]">Dual currency model • Separate school & home economies</p>
      </div>

      {/* Achievement Popup */}
      <AnimatePresence>
        {newAchievement && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: -50 }}
            className="absolute top-20 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="px-6 py-4 rounded-2xl bg-gradient-to-r from-[#ffd700]/20 to-[#f59e0b]/20 border-2 border-[#ffd700] shadow-lg shadow-[#ffd700]/20">
              <div className="flex items-center gap-4">
                <span className="text-5xl">{newAchievement.icon}</span>
                <div>
                  <p className="text-xs text-[#ffd700] uppercase tracking-wider font-semibold">Achievement Unlocked!</p>
                  <p className="text-xl font-bold text-white">{newAchievement.title}</p>
                  <p className="text-sm text-[#94a3b8]">+{newAchievement.reward} 🪙 Gold Coins</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reward Purchase Popup */}
      <AnimatePresence>
        {purchasedReward && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50"
          >
            <div className="px-8 py-6 rounded-2xl bg-gradient-to-r from-[#10b981]/20 to-[#0ea5e9]/20 border-2 border-[#10b981] shadow-lg">
              <div className="text-center">
                <span className="text-6xl block mb-2">{purchasedReward.icon}</span>
                <p className="text-xs text-[#10b981] uppercase tracking-wider font-semibold mb-1">Reward Claimed!</p>
                <p className="text-xl font-bold text-white">{purchasedReward.title}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Coin Animation */}
      <AnimatePresence>
        {floatingCoin && (
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 0, y: -40 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute top-24 left-1/2 -translate-x-1/2 z-40 text-2xl font-bold"
            style={{ color: floatingCoin.currency === 'gold' ? '#ffd700' : '#c0c0c0' }}
          >
            +{floatingCoin.amount} {floatingCoin.currency === 'gold' ? '🪙' : '🥈'}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 px-6 pb-2 min-h-0">
        {/* Left: Balances & Transaction Log */}
        <div className="w-72 flex flex-col gap-3">
          {/* Gold Balance */}
          <motion.div
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)',
              border: '2px solid rgba(255, 215, 0, 0.4)',
            }}
            animate={{ scale: floatingCoin?.currency === 'gold' ? [1, 1.02, 1] : 1 }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#ffd700]">School Gold</span>
              <span className="text-xs text-[#94a3b8]">Teacher Managed</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl">🪙</span>
              <span className="text-3xl font-bold text-[#ffd700]">{goldBalance}</span>
            </div>
          </motion.div>

          {/* Silver Balance */}
          <motion.div
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.15) 0%, rgba(148, 163, 184, 0.1) 100%)',
              border: '2px solid rgba(192, 192, 192, 0.4)',
            }}
            animate={{ scale: floatingCoin?.currency === 'silver' ? [1, 1.02, 1] : 1 }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#c0c0c0]">Home Silver</span>
              <span className="text-xs text-[#94a3b8]">Parent Managed</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl">🥈</span>
              <span className="text-3xl font-bold text-[#c0c0c0]">{silverBalance}</span>
            </div>
          </motion.div>

          {/* Transaction Log */}
          <div className="flex-1 rounded-xl p-3 overflow-hidden" style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2 block">Recent Activity</span>
            <div className="space-y-1.5 overflow-hidden">
              <AnimatePresence mode="popLayout">
                {transactions.map((tx) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -20, height: 0 }}
                    animate={{ opacity: 1, x: 0, height: 'auto' }}
                    exit={{ opacity: 0, x: 20 }}
                    className="flex items-center justify-between text-xs py-1 border-b border-[#334155]/50"
                  >
                    <span className="text-[#94a3b8] truncate flex-1">{tx.source}</span>
                    <span className={`font-medium ml-2 ${tx.type === 'earned' ? 'text-[#10b981]' : 'text-[#f59e0b]'}`}>
                      {tx.type === 'earned' ? '+' : '-'}{tx.amount} {tx.currency === 'gold' ? '🪙' : '🥈'}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
              {transactions.length === 0 && (
                <p className="text-[#64748b] text-xs text-center py-4">No activity yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Center: Reward Shops */}
        <div className="flex-1 flex flex-col gap-3">
          {/* School Rewards */}
          <div className="flex-1 rounded-xl p-4" style={{ background: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏫</span>
              <span className="text-sm font-semibold text-white">School Reward Shop</span>
              <span className="text-xs text-[#64748b] ml-auto">Gold Coins</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SCHOOL_REWARDS.map((reward) => (
                <div
                  key={reward.id}
                  className={`p-3 rounded-lg transition-all ${
                    goldBalance >= reward.cost
                      ? 'bg-[#1e293b] border border-[#ffd700]/30'
                      : 'bg-[#1e293b]/50 border border-[#334155] opacity-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{reward.icon}</span>
                    <span className="text-sm font-medium text-white">{reward.title}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-[#ffd700] font-bold">{reward.cost}</span>
                    <span className="text-[#ffd700]">🪙</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Home Rewards */}
          <div className="flex-1 rounded-xl p-4" style={{ background: 'rgba(192, 192, 192, 0.05)', border: '1px solid rgba(192, 192, 192, 0.2)' }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏠</span>
              <span className="text-sm font-semibold text-white">Home Reward Shop</span>
              <span className="text-xs text-[#64748b] ml-auto">Silver Coins</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {HOME_REWARDS.map((reward) => (
                <div
                  key={reward.id}
                  className={`p-3 rounded-lg transition-all ${
                    silverBalance >= reward.cost
                      ? 'bg-[#1e293b] border border-[#c0c0c0]/30'
                      : 'bg-[#1e293b]/50 border border-[#334155] opacity-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{reward.icon}</span>
                    <span className="text-sm font-medium text-white">{reward.title}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-[#c0c0c0] font-bold">{reward.cost}</span>
                    <span className="text-[#c0c0c0]">🥈</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Achievements */}
        <div className="w-64 flex flex-col">
          <div className="flex-1 rounded-xl p-4" style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">🏆</span>
              <span className="text-sm font-semibold text-white">Achievements</span>
            </div>
            <div className="space-y-3">
              {achievements.map((achievement) => (
                <motion.div
                  key={achievement.id}
                  className={`p-3 rounded-lg transition-all ${
                    achievement.unlocked
                      ? 'bg-gradient-to-r from-[#ffd700]/10 to-[#f59e0b]/5 border border-[#ffd700]/30'
                      : 'bg-[#1e293b]/50 border border-[#334155]'
                  }`}
                  animate={achievement.unlocked ? { scale: [1, 1.02, 1] } : {}}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl ${achievement.unlocked ? '' : 'grayscale opacity-40'}`}>
                      {achievement.icon}
                    </span>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${achievement.unlocked ? 'text-white' : 'text-[#64748b]'}`}>
                        {achievement.title}
                      </p>
                      <p className="text-xs text-[#64748b]">{achievement.description}</p>
                    </div>
                    {achievement.unlocked && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="text-lg"
                      >
                        ✅
                      </motion.span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-[#ffd700]">
                    +{achievement.reward} 🪙
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-6 py-2 flex items-center justify-center gap-4" style={{ background: 'rgba(15,23,42,0.9)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="flex items-center gap-1.5 px-5 py-2 rounded-lg font-medium text-sm"
          style={{
            background: isPlaying ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
            border: `2px solid ${isPlaying ? 'rgba(239,68,68,0.5)' : 'rgba(16,185,129,0.5)'}`,
            color: isPlaying ? '#ef4444' : '#10b981',
          }}
        >
          {isPlaying ? <><PauseIcon className="w-4 h-4" /> Pause</> : <><PlayIcon className="w-4 h-4" /> {transactions.length > 0 ? 'Resume' : 'Start Demo'}</>}
        </button>
        <button onClick={reset} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-[#94a3b8] hover:text-white hover:bg-[#334155]">
          <ArrowPathIcon className="w-4 h-4" /> Reset
        </button>
        <div className="text-sm text-[#64748b]">Watch dual currencies, achievements & reward redemption</div>
      </div>
    </div>
  );
};

export default TeacherSlide10RewardsDemo;
